import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Stethoscope, Megaphone, Hourglass, Users, Eye, Clock as ClockIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtISTTime() {
  return new Date().toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit", minute: "2-digit",
  });
}
function fmtISTDate() {
  return new Date().toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    weekday: "long", day: "2-digit", month: "long", year: "numeric",
  });
}

// Distinct accent palettes for up to 6 doctor columns.
// Each palette themes the column header, "Doctor Calling" tile and "In Consultation" rows together.
const ACCENTS = [
  { name: "blue", header: "bg-blue-600", panel: "bg-blue-50", calling: "bg-blue-600", callingFg: "text-white", inConsult: "bg-blue-100", inConsultFg: "text-blue-900", border: "border-blue-300" },
  { name: "emerald", header: "bg-emerald-600", panel: "bg-emerald-50", calling: "bg-emerald-600", callingFg: "text-white", inConsult: "bg-emerald-100", inConsultFg: "text-emerald-900", border: "border-emerald-300" },
  { name: "violet", header: "bg-violet-600", panel: "bg-violet-50", calling: "bg-violet-600", callingFg: "text-white", inConsult: "bg-violet-100", inConsultFg: "text-violet-900", border: "border-violet-300" },
  { name: "amber", header: "bg-amber-600", panel: "bg-amber-50", calling: "bg-amber-600", callingFg: "text-white", inConsult: "bg-amber-100", inConsultFg: "text-amber-900", border: "border-amber-300" },
  { name: "rose", header: "bg-rose-600", panel: "bg-rose-50", calling: "bg-rose-600", callingFg: "text-white", inConsult: "bg-rose-100", inConsultFg: "text-rose-900", border: "border-rose-300" },
  { name: "teal", header: "bg-teal-600", panel: "bg-teal-50", calling: "bg-teal-600", callingFg: "text-white", inConsult: "bg-teal-100", inConsultFg: "text-teal-900", border: "border-teal-300" },
];

function speak(text) {
  try {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = "en-IN";
    u.rate = 0.92;
    u.pitch = 1.0;
    u.volume = 1.0;
    window.speechSynthesis.speak(u);
  } catch (_) { /* ignore */ }
}

export default function PublicBoard() {
  const { branchId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [time, setTime] = useState(fmtISTTime());
  const [today, setToday] = useState(fmtISTDate());
  const [muted, setMuted] = useState(true);  // start muted until user interacts (browser policy)
  const announcedRef = useRef(new Set());     // token numbers we've already announced
  const firstLoadRef = useRef(true);          // skip announcing on the very first poll

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/patients/visits/board/${branchId}`);
        // Announce tokens that transitioned into "called" since the last poll (skip on first load)
        if (!firstLoadRef.current) {
          const newlyCalled = r.data.items.filter((v) => v.status === "called" && !announcedRef.current.has(v.token_no));
          for (const v of newlyCalled) {
            announcedRef.current.add(v.token_no);
            if (!muted) {
              const docPart = v.doctor_name ? ` Please proceed to Dr. ${v.doctor_name}'s consultation room.` : " Please proceed to the consultation room.";
              speak(`Token number ${v.token_no}.${docPart}`);
            }
          }
        } else {
          // First load: seed announcedRef with currently-called tokens so we don't announce them on first paint.
          r.data.items.filter((v) => v.status === "called").forEach((v) => announcedRef.current.add(v.token_no));
          firstLoadRef.current = false;
        }
        // Drop tokens no longer 'called' so they can re-announce later if re-called
        const stillCalled = new Set(r.data.items.filter((v) => v.status === "called").map((v) => v.token_no));
        for (const tk of Array.from(announcedRef.current)) {
          if (!stillCalled.has(tk)) announcedRef.current.delete(tk);
        }
        setData(r.data); setError("");
      } catch (e) { setError("Could not load the queue. Check the branch link."); }
    };
    fetch();
    const i = setInterval(fetch, 6000);
    const c = setInterval(() => { setTime(fmtISTTime()); setToday(fmtISTDate()); }, 15000);
    return () => { clearInterval(i); clearInterval(c); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, muted]);

  if (error) return <div className="grid h-screen place-items-center bg-slate-100 text-rose-700">{error}</div>;
  if (!data) return <div className="grid h-screen place-items-center bg-slate-100 text-slate-500">Loading queue…</div>;

  // Group visits per doctor; "Unassigned" bucket for walk-ins
  const byDoctor = {};
  for (const v of data.items) {
    const key = v.doctor_name ? `Dr. ${v.doctor_name}` : "Unassigned";
    if (!byDoctor[key]) byDoctor[key] = [];
    byDoctor[key].push(v);
  }
  const doctors = Object.keys(byDoctor).sort();
  // Stable color assignment per doctor (so a doctor always shows the same accent across refreshes)
  const accentFor = (doctor) => {
    let h = 0;
    for (let i = 0; i < doctor.length; i += 1) h = (h * 31 + doctor.charCodeAt(i)) >>> 0;
    return ACCENTS[h % ACCENTS.length];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40 p-4 lg:p-6" data-testid="public-board">
      {/* Header — three-column layout matching reference design */}
      <header className="mb-5 grid grid-cols-1 items-center gap-3 rounded-2xl border border-border bg-white px-6 py-4 shadow-sm md:grid-cols-3">
        {/* Left: brand */}
        <div className="flex items-center gap-3">
          {data.hospital?.logo_url
            ? <img src={data.hospital.logo_url} alt="" className="h-14 w-14 rounded-lg object-contain" />
            : <div className="grid h-14 w-14 place-items-center rounded-lg bg-brand text-white"><Eye size={28} /></div>}
          <div>
            <div className="font-heading text-2xl font-bold uppercase tracking-tight text-ink">Patient Queue Display</div>
            <div className="text-xs text-ink-muted">{data.hospital?.name || "CareO Eye"} · {data.branch.name} ({data.branch.code})</div>
          </div>
        </div>
        {/* Middle: department */}
        <div className="text-center">
          <div className="inline-block border-b-2 border-brand pb-1">
            <div className="font-heading text-xl font-semibold text-brand">Eye Care OPD</div>
          </div>
        </div>
        {/* Right: clock + mute */}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => { setMuted((m) => !m); if (muted) speak("Voice announcements activated."); }}
            className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium shadow-sm ${muted ? "bg-rose-100 text-rose-700 hover:bg-rose-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}
            data-testid="board-voice-toggle"
          >
            <Megaphone size={12} /> {muted ? "Voice OFF — tap to enable" : "Voice ON"}
          </button>
          <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-1.5">
            <ClockIcon size={16} className="text-brand" />
            <div className="text-right">
              <div className="font-mono text-lg font-semibold leading-none text-ink">{time}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted">{today}</div>
            </div>
          </div>
        </div>
      </header>

      {/* Doctor columns */}
      {doctors.length === 0 ? (
        <div className="grid place-items-center rounded-xl border border-dashed border-border bg-white p-16 text-ink-muted">No visits today yet.</div>
      ) : (
        <div className={`grid gap-4 ${doctors.length === 1 ? "" : doctors.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3 xl:grid-cols-3"}`}>
          {doctors.map((doc) => {
            const accent = accentFor(doc);
            const visits = byDoctor[doc];
            const calling = visits.filter((v) => v.status === "called");
            const inConsult = visits.filter((v) => v.status === "in_consultation");
            const waiting = visits.filter((v) => v.status === "waiting");
            return (
              <div key={doc} className={`flex flex-col overflow-hidden rounded-2xl border-2 ${accent.border} bg-white shadow-md`} data-testid={`board-doctor-${doc}`}>
                {/* Doctor header */}
                <div className={`${accent.header} flex items-center gap-2 px-4 py-3 text-white`}>
                  <div className="grid h-8 w-8 place-items-center rounded-full bg-white/20"><Stethoscope size={16} /></div>
                  <span className="font-heading text-lg font-bold tracking-tight">{doc}</span>
                </div>

                {/* Doctor Calling — large prominent tile */}
                <div className={`${accent.panel} px-4 pb-4 pt-3`}>
                  <div className="mb-2 flex items-center gap-1.5">
                    <Megaphone size={12} className="text-ink" />
                    <div className="text-[10px] font-bold uppercase tracking-widest text-ink">Doctor Calling</div>
                  </div>
                  {calling.length === 0 ? (
                    <div className="grid h-24 place-items-center rounded-lg border border-dashed border-border bg-white/60 text-xs text-ink-subtle">— no patient being called —</div>
                  ) : (
                    calling.map((v) => (
                      <div key={v.token_no} className={`${accent.calling} ${accent.callingFg} mb-2 rounded-xl p-4 shadow-md ring-2 ring-white animate-pulse-glow`} data-testid={`board-calling-${v.token_no}`}>
                        <div className="text-[10px] uppercase tracking-widest opacity-80">Now Calling</div>
                        <div className="font-heading text-5xl font-black leading-none tracking-tight">#{v.token_no}</div>
                        <div className="mt-2 text-lg font-semibold">{v.patient_name_masked}</div>
                        <div className="text-xs opacity-80">UHID {v.patient_uhid}</div>
                      </div>
                    ))
                  )}
                </div>

                {/* In Consultation */}
                <div className="border-y border-border/60 bg-white px-4 py-3">
                  <div className="mb-2 flex items-center gap-1.5">
                    <Stethoscope size={12} className="text-ink" />
                    <div className="text-[10px] font-bold uppercase tracking-widest text-ink">In Consultation</div>
                  </div>
                  {inConsult.length === 0 ? (
                    <div className="py-2 text-center text-xs text-ink-subtle">—</div>
                  ) : (
                    inConsult.map((v) => (
                      <div key={v.token_no} className={`${accent.inConsult} ${accent.inConsultFg} mb-1 flex items-center justify-between rounded-lg px-3 py-2`} data-testid={`board-in-consult-${v.token_no}`}>
                        <div className="flex items-baseline gap-3">
                          <span className="font-heading text-2xl font-bold">#{v.token_no}</span>
                          <span className="font-medium">{v.patient_name_masked}</span>
                        </div>
                        <span className="text-[10px] uppercase tracking-wider opacity-75">UHID {v.patient_uhid}</span>
                      </div>
                    ))
                  )}
                </div>

                {/* Waiting list */}
                <div className="bg-white px-4 pb-4 pt-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Hourglass size={12} className="text-ink" />
                      <div className="text-[10px] font-bold uppercase tracking-widest text-ink">Waiting</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-ink-muted"><Users size={10} />{waiting.length}</span>
                  </div>
                  {waiting.length === 0 ? (
                    <div className="py-2 text-center text-xs text-ink-subtle">— queue empty —</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase tracking-wider text-ink-muted">
                        <tr className="border-b border-border">
                          <th className="py-1 text-left">Token No.</th>
                          <th className="py-1 text-left">UHID No.</th>
                          <th className="py-1 text-left">Patient Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waiting.slice(0, 8).map((v) => (
                          <tr key={v.token_no} className="border-b border-border/40 last:border-0" data-testid={`board-waiting-${v.token_no}`}>
                            <td className="py-1.5 font-mono font-bold text-ink">#{v.token_no}</td>
                            <td className="py-1.5 font-mono text-[11px] text-ink-muted">{v.patient_uhid}</td>
                            <td className="py-1.5 font-medium text-ink">{v.patient_name_masked}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {waiting.length > 8 && <div className="pt-1 text-center text-[10px] text-ink-muted">…and {waiting.length - 8} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <footer className="mt-5 rounded-lg bg-white/70 px-4 py-2 text-center text-[11px] text-ink-muted shadow-sm">
        Names are partially masked for patient privacy · {muted ? <b>Tap "Voice OFF" above to enable spoken announcements</b> : <span className="text-emerald-700"><b>Voice ON</b> — token numbers will be announced as patients are called</span>}
      </footer>
    </div>
  );
}
